import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { Button } from '../Button';
import {
  ArrowGroup,
  CarouselHeader,
  CarouselTitle,
  CarouselTrack,
  CarouselWrapper,
  ProgressBarInner,
  ProgressBarOuter,
  ProgressTrack,
} from './Carousel.styles';
import type { CarouselProps, ResponsiveVisibleCount } from './Carousel.types';

export const Carousel = ({
  children,
  title,
  visibleCount,
  scrollAmount,
  showProgress = false,
  controlsVariant = 'neutral',
}: CarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollLeft(track.scrollLeft > 0);
    setCanScrollRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);

    const maxScroll = track.scrollWidth - track.clientWidth;
    setScrollProgress(maxScroll > 0 ? track.scrollLeft / maxScroll : 0);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateArrows();

    track.addEventListener('scroll', updateArrows, { passive: true });

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateArrows) : null;
    observer?.observe(track);

    return () => {
      track.removeEventListener('scroll', updateArrows);
      observer?.disconnect();
    };
  }, [updateArrows]);

  const getScrollAmount = () => {
    if (scrollAmount) return scrollAmount;
    const track = trackRef.current;
    if (track) return track.clientWidth;
    return 300;
  };

  const scrollLeft = () => {
    trackRef.current?.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
  };

  const scrollRight = () => {
    trackRef.current?.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
  };

  const computeChildWidth = (count: number) => {
    const gap = Math.round((count - 1) * 16 * 100) / 100;
    return `calc((100% - ${gap}px) / ${count})`;
  };

  const buildTrackStyle = (): React.CSSProperties | undefined => {
    if (!visibleCount) return undefined;

    if (typeof visibleCount === 'number') {
      if (visibleCount <= 0) return undefined;
      return { '--carousel-child-width': computeChildWidth(visibleCount) } as React.CSSProperties;
    }

    const { mobile, desktop } = visibleCount as ResponsiveVisibleCount;
    const style: Record<string, string> = {};
    if (desktop && desktop > 0) style['--carousel-child-width'] = computeChildWidth(desktop);
    if (mobile && mobile > 0) style['--carousel-child-width-mobile'] = computeChildWidth(mobile);
    return Object.keys(style).length > 0 ? (style as unknown as React.CSSProperties) : undefined;
  };

  const trackStyle = buildTrackStyle();

  const arrows = (
    <ArrowGroup data-variant={controlsVariant}>
      <Button
        variant="icon-ghost"
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        aria-label="Scroll left"
      >
        <ChevronLeftIcon size={20} />
      </Button>
      <Button
        variant="icon-ghost"
        onClick={scrollRight}
        disabled={!canScrollRight}
        aria-label="Scroll right"
      >
        <ChevronRightIcon size={20} />
      </Button>
    </ArrowGroup>
  );

  return (
    <CarouselWrapper>
      {(title || canScrollLeft || canScrollRight) && (
        <CarouselHeader>
          {title ? (
            typeof title === 'string' ? (
              <CarouselTitle>{title}</CarouselTitle>
            ) : (
              title
            )
          ) : (
            <span />
          )}
          {arrows}
        </CarouselHeader>
      )}
      <CarouselTrack ref={trackRef} style={trackStyle} data-testid="carousel-track">
        {children}
      </CarouselTrack>
      {showProgress && (canScrollLeft || canScrollRight) && (
        <ProgressTrack>
          <ProgressBarOuter>
            <ProgressBarInner
              data-testid="carousel-progress"
              style={{ width: '40%', left: `${scrollProgress * 60}%` }}
            />
          </ProgressBarOuter>
        </ProgressTrack>
      )}
    </CarouselWrapper>
  );
};
